import React, { useEffect, useRef, useState } from 'react';
import logo from '../assets/images/logo.png';
import LanguageSelector from './LanguageSelector';
import { LANGUAGES } from '../constants';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import downloadPlayEn from '../assets/images/download-play-en.png';
import downloadPlayAr from '../assets/images/download-play-ar.png';
import downloadAppstoreEn from '../assets/images/download-appstore-en.png';
import downloadAppstoreAr from '../assets/images/download-appstore-ar.png';
import playQr from '../assets/images/play-qr.svg';
import appstoreQr from '../assets/images/appstore-qr.svg';
import { colors } from '../theme/colors';
import SavingsCard from './SavingsCard';
import StepCard from './StepCard';
import GraphicsSectionDesktop from './GraphicsSectionDesktop';
import GraphicsSectionMobile from './GraphicsSectionMobile';
import percentageIcon from '../assets/images/landing/percentage-icon.png';
import favoritesIcon from '../assets/images/landing/favorites-icon.png';
import notificationsIcon from '../assets/images/landing/notifications-icon.png';
import shareIcon from '../assets/images/landing/share-icon.png';
import copyIcon from '../assets/images/landing/copy-icon.png';
import promoImageEn from '../assets/images/promo-image-en.png';
import promoImageAr from '../assets/images/promo-image-ar.png';
import promoLocationIcon from '../assets/images/promo-location-icon.png';
import promoVerificationIcon from '../assets/images/promo-verification-icon.png';
import promoFlashIcon from '../assets/images/promo-flash-icon.png';
import riyalIcon from '../assets/images/riyal-icon.png';
import featureImageEn from '../assets/images/feature-image-en.png';
import featureImageAr from '../assets/images/feature-image-ar.png';
import instagramIcon from '../assets/images/instagram-icon.png';
import twitterIcon from '../assets/images/twitter-icon.svg';
import tiktokIcon from '../assets/images/tiktok-icon.png';
import heroVideo from '../assets/videos/hero-video.mp4';

// load partner logos from assets/images/logos
const logosContext = require.context('../assets/images/logos', false, /\.(png|jpe?g|svg)$/);
const partnerLogos = logosContext.keys().map((k) => logosContext(k));
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.blupon.mobile';
const APP_STORE_URL = 'https://apps.apple.com/us/app/blupon-%D8%A8%D9%84%D9%88%D8%A8%D9%88%D9%86/id6751548902';
const MOBILE_BREAKPOINT = 768;

const styles = {
  section: {
    background: colors.primary.main,
    color: '#fff',
    padding: '48px 84px',
    '@media (max-width: 768px)': {
      padding: '32px 16px'
    }
  },
  h2: {
    marginTop: 20,
    marginBottom: 0,
    fontSize: 55,
    fontWeight: 700,
    '@media (max-width: 768px)': {
      fontSize: 32
    }
  },
  description: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: 500,
    maxWidth: 450,
    '@media (max-width: 768px)': {
      fontSize: 13
    }
  },
  h3: {
    marginTop: 16,
    fontSize: 20,
    fontWeight: 500,
    fontStyle: 'semibold',
    '@media (max-width: 768px)': {
      fontSize: 16
    }
  },
  storeButtonsContainer: {
    display: 'flex',
    gap: 30,
    marginTop: 20,
    flexWrap: 'wrap',
    justifyContent: 'center',
    '@media (max-width: 768px)': {
      flexWrap: 'nowrap',
      gap: 20
    }
  },
  qrContainer: {
    display: 'flex',
    gap: 25,
    marginTop: 20,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    '@media (max-width: 768px)': {
      flexWrap: 'nowrap',
      gap: 15,
      flexDirection: 'row'
    }
  },
  qrImage: {
    '@media (max-width: 768px)': {
      height: '80px'
    }
  }
};

const Landing = () => {
  const { t } = useTranslation();
  const stored = (localStorage.getItem('language') || 'EN').toUpperCase();
  const [language, setLanguage] = useState(stored);
  const [isMobileGraphics, setIsMobileGraphics] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const videoRef = useRef(null);
  const dir = LANGUAGES[language]?.dir || 'ltr';

  // choose promo background depending on language
  const phoneBg = language === 'AR' ? promoImageAr : promoImageEn;

  const countryOptions = [
    { value: 'Kingdom of Saudi Arabia', label: 'Kingdom of Saudi Arabia' }
  ];

  const handleLanguageChange = ({ target }) => {
    const code = (target?.value || 'EN').toUpperCase();
    setLanguage(code);
    localStorage.setItem('language', code);
    try {
      i18next.changeLanguage(code);
      document.documentElement.setAttribute('dir', LANGUAGES[code].dir);
      document.body.setAttribute('dir', LANGUAGES[code].dir);
    } catch (e) {
      console.warn(e);
    }
  };

  const handleCountryChange = () => {};

  const playImg = language === 'AR' ? downloadPlayAr : downloadPlayEn;
  const appstoreImg = language === 'AR' ? downloadAppstoreAr : downloadAppstoreEn;

  const getResponsiveStyle = (baseStyles, responsiveOverrides) => {
    const { '@media (max-width: 768px)': mobileStyles, ...desktopStyles } = baseStyles;
    return desktopStyles;
  };

  const savingsCards = [
    { icon: copyIcon, title: t('savings.cards.2.title'), description: t('savings.cards.2.description') },
    { icon: shareIcon, title: t('savings.cards.1.title'), description: t('savings.cards.1.description') },
    { icon: percentageIcon, title: t('savings.cards.0.title'), description: t('savings.cards.0.description') },
    { icon: notificationsIcon, title: t('savings.cards.4.title'), description: t('savings.cards.4.description') },
    { icon: favoritesIcon, title: t('savings.cards.3.title'), description: t('savings.cards.3.description') },
  ];

  const stepsCards = [
    { title: t('steps.cards.0.title'), description: t('steps.cards.0.description') },
    { title: t('steps.cards.1.title'), description: t('steps.cards.1.description') },
    { title: t('steps.cards.2.title'), description: t('steps.cards.2.description') }
  ];

  const graphicsItems = [
    { icon: promoLocationIcon, title: t('graphics.items.0.title'), description: t('graphics.items.0.description') },
    { icon: promoVerificationIcon, title: t('graphics.items.1.title'), description: t('graphics.items.1.description') },
    { icon: promoFlashIcon, title: t('graphics.items.2.title'), description: t('graphics.items.2.description') }
  ];

  const scrollToSection = (event, ref, block = 'start') => {
    event.preventDefault();
    ref.current?.scrollIntoView({ behavior: 'smooth', block });
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    function updateGraphicsViewport() {
      if (typeof window === 'undefined') return;
      setIsMobileGraphics(window.innerWidth <= MOBILE_BREAKPOINT);
    }

    updateGraphicsViewport();
    window.addEventListener('resize', updateGraphicsViewport);
    return () => window.removeEventListener('resize', updateGraphicsViewport);
  }, []);

  return (
    <div ref={topRef} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }} dir={dir}>
      <style>{`
        .landing-header {
          direction: ltr !important;
          position: relative;
          z-index: 3;
        }
        .landing-language-selector [class*="select__control"] {
          min-height: 40px !important;
        }
        .landing-section { position: relative; overflow: hidden; }
        .landing-hero-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; z-index: 0; pointer-events: none; }
        .landing-hero-overlay-base { position: absolute; inset: 0; background: rgba(42, 111, 152, 0.5); z-index: 1; pointer-events: none; }
        .landing-hero-overlay-tint { position: absolute; inset: 0; background: rgb(59, 156, 214); mix-blend-mode: color; z-index: 2; pointer-events: none; }
        .landing-section > .landing-content { position: relative; z-index: 3; }
        .landing-store-buttons {
          direction: ltr !important;
          margin-top: 8px !important;
        }
        .landing-description {
          white-space: pre-wrap !important;
        }
        .partners-section { padding: 64px 24px; }
        .partners-row { gap: 0; justify-content: center; }
        .partner-item { padding: 20px 24px; display: inline-flex; }
        .partner-img { height: 48px; max-width: 120px; }
        @media (max-width: 768px) {
          .landing-section {
            padding: 0px 16px 70px 16px !important;
          }
          .landing-header {
            padding: 0 !important;
            position: relative !important;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 24px;
          }
          .landing-language-selector {
            position: static !important;
            z-index: auto !important;
            margin-top: -12px;
          }
          .landing-logo {
            position: static !important;
            z-index: auto !important;
            height: 72px !important;
            margin-top: 10px;
          }
          .landing-h2 {
            font-size: 34px !important;
            margin-top: 20px !important;
            margin-bottom: 20px !important;
            line-height: 1.2 !important;
          }
          .landing-description {
            font-size: 16px !important;
            margin-bottom: 24px !important;
            white-space: pre-wrap !important;
            font-weight: 500 !important;
          }
          .landing-h3 {
            font-size: 20px !important;
            font-weight: 500 !important;
          }
          .landing-store-buttons {
            flex-wrap: nowrap !important;
            gap: 20px !important;
            margin-top: 12px !important;
          }
          .landing-store-buttons a img {
            height: 68px !important;
          }
          .landing-qr-container {
            display: none !important;
          }
          .partners-section { padding: 32px 12px !important; }
          .partners-row { gap: 12px !important; }
          .partners-row { max-width: 100% !important; padding: 0 8px !important; justify-content: center !important; }
          .partner-img { max-width: 100% !important; height: auto !important; }
          .partner-item { padding: 6px !important; }
          .partner-img { height: 42px !important; }
          .landing-hero-video { display: block !important; }
        }

        /* prevent horizontal overflow on small screens */
        .landing-section,
        .partners-section,
        .partners-row { box-sizing: border-box; max-width: 100%; overflow-x: hidden; }

        /* allow vertical overflow for savings so drop-shadows are not clipped */
        .savings-section,
        .savings-inner { box-sizing: border-box; max-width: 100%; overflow: visible; }

        /* savings inner width control */ 
        .savings-section > .savings-inner {
          width: 80%;
          max-width: 1280px;
          margin: 0 auto;
          text-align: center;
        }
        @media (max-width: 992px) {
          .savings-section > .savings-inner { width: 100%; padding: 0 16px; }
        }
        @media (max-width: 420px) {
          .savings-section { padding-left: 8px !important; padding-right: 8px !important; }
          .savings-section > .savings-inner { padding: 0 8px !important; width: 100%; }
          .savings-title { margin-bottom: 24px !important; }
        }

        /* Start Saving button adjustments */
        .start-saving-button { font-size: 16px; }
        @media (max-width: 420px) {
          .start-saving-button { font-size: 20px !important; font-weight: 400 !important; padding: 12px 28px !important; border-radius: 32px !important; }
        }

        /* Steps section styling */
        .steps-section { background: #fff; padding: 60px 24px 24px; }
        .steps-title { font-size: 24px; margin: 0 0 18px; color: #222; font-weight: 600; text-align: center; }
        .steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; max-width: 1100px; margin: 0 auto; }
        @media (max-width: 992px) {
          .steps-grid { grid-template-columns: 1fr; gap: 30px; max-width: 420px; }
          .steps-section { padding: 40px 16px 1px; }
        }
        @media (max-width: 420px) {
          .steps-title { font-size: 24px; margin-bottom: 40px; }
        }

        /* Built section mobile adjustments */
        @media (max-width: 768px) {
          .built-content-wrapper { padding: 20px; border-radius: 12px; }
        }
        /* Built section styling */
        .built-section { padding: 40px 24px; margin: 0 auto;}
        .built-content-wrapper {
          width: 100%;
          max-width: 950px;
          margin: 0 auto;
          background: ${colors.primary.main};
          border-radius: 16px;
          padding: 48px 50px 36px 50px;
          box-sizing: border-box;
        }
        .built-inner { max-width: 1200px; margin: 0 auto; color: #fff; }
        .built-title { font-size: 26px; font-weight: 600; margin: 0 0 56px; text-align: center; }
        .built-grid { display: flex; gap: 28px; }
        .built-item { flex: 1; text-align: center; position: relative; margin: 0px 0px; }
        .built-number { font-size: 42px; font-weight: 700; text-align: center; display: inline-flex; align-items: center; gap: 8px; 
                        justify-content: center; margin-bottom: 12px; }
        .built-number .built-riyal { width: 32px; height: auto; display: inline-block; }
        .built-desc { font-size: 13px; font-weight: 400; color: rgba(255,255,255,0.95); text-align: center; white-space: pre-wrap; line-height: 1.5; }
        .built-cta { margin-top: 36px; text-align: center; }
          .built-cta-button { background: #fff; color: #000; padding: 10px 20px; border-radius: 24px; display: inline-block; text-decoration: none; font-weight: 400; font-size: 14px; }
        @media (max-width: 992px) {
          .built-section { padding: 40px 0px; }
          .built-content-wrapper { max-width: calc(100% - 32px); margin: 0 16px; padding: 55px 24px; }
          .built-title { font-size: 24px; margin-bottom: 24px; }
          .built-number { font-size: 34px; font-weight: 600; }
          .built-desc { font-size: 16px; }
          .built-grid { display: block; }
          .built-item { margin-bottom: 16px; }
          .built-cta { margin-top: 20px; }
          .built-cta-button { margin-top: 12px; font-size: 16px; padding: 12px 28px;}
        }
        /* Feature section styling */
        .feature-section { background: #1a1f26; padding: 80px 100px; }
        .feature-container { max-width: 1200px; margin: 0 auto; display: flex; gap: 60px; align-items: center; }
        .feature-text { flex: 1; display: flex; flex-direction: column; color: #fff; }
        .feature-title { font-size: 36px; font-weight: 600; margin: 0 0 8px; color: #fff; }
        .feature-description { font-size: 16px; font-weight: 400; margin: 0 0 32px; color: rgba(255,255,255); line-height: 1.6; white-space: pre-wrap !important; }
        .feature-store-buttons { display: flex; gap: 20px; margin-bottom: 32px; }
        .feature-store-buttons img { height: 54px; }
        .feature-graphic { flex: 1; display: flex; align-items: center; justify-content: center; }
        .feature-graphic img { max-width: 100%; height: auto; }
        .feature-footer { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-top: 100px; color: rgba(255,255,255,0.9); flex-wrap: wrap; padding: 0 24px; }
        .feature-footer-left { display: flex; flex-direction: column; gap: 8px; }
        .feature-footer .follow-label { font-size: 16px; font-weight: 400; }
        .social-icons { display: flex; gap: 8px; }
        .social-icons a { display: inline-flex; align-items: center; justify-content: center; margin: 2px; border-radius: 6px; color: #fff; text-decoration: none; }
        .social-icons img { width: 25px; height: 25px; display: block; }
        .feature-footer-right { display: flex; gap: 28px; align-items: center; }
        .feature-footer .footer-link { color: rgba(255,255,255,0.9); text-decoration: none; font-size: 16px; font-weight: 400; }
        
        /* RTL styles for feature section */
        .feature-section[dir="rtl"] .feature-container { flex-direction: row-reverse; }
        .feature-section[dir="rtl"] .feature-text { align-items: flex-end; text-align: right; }

        @media (max-width: 992px) {
          .feature-section { padding: 60px 16px; }
          .feature-container { flex-direction: column; gap: 40px; }
          .feature-section[dir="rtl"] .feature-container { flex-direction: column; }
          .feature-text { align-items: center; text-align: center; }
          .feature-section[dir="rtl"] .feature-text { align-items: center; text-align: center; }
          .feature-title { font-size: 28px; }
          .feature-description { font-size: 16px; }
          .feature-store-buttons { flex-wrap: wrap; justify-content: center; }
          .feature-qr-container { justify-content: center; }
        }
        @media (max-width: 768px) {
          .feature-qr-container { display: none !important; }
          .feature-container { gap: 20px; }
          .feature-store-buttons { margin-bottom: 12px; width: 100%; justify-content: space-between; }
          .feature-description { margin-bottom: 40px; }
          .feature-store-buttons img { height: 65px; }
          .feature-text { align-items: flex-start; text-align: start; }
          .feature-section[dir="rtl"] .feature-text { align-items: flex-end; }
          .feature-title { font-size: 20px; }

          /* Mobile footer: stack vertically. In LTR align left, in RTL align right. */
          .feature-footer { flex-direction: column; align-items: flex-start; width: 100%; }
          .feature-section[dir="rtl"] .feature-footer { align-items: flex-end; }

          /* Ensure left block (follow + icons) stays first and spans full width */
          .feature-footer-left { width: 100%; }
          .feature-footer-left .social-icons { margin-top: 6px; }
          /* Larger social icons on mobile for better touch targets */
          .feature-footer-left .social-icons img { width: 30px; height: 30px; }

          /* Stack policy links vertically and align with direction */
          .feature-footer-right { margin-top: 12px; width: 100%; display: flex; flex-direction: column; gap: 24px; align-items: flex-start; }
          .feature-section[dir="rtl"] .feature-footer-right { align-items: flex-end; }

          /* Increase mobile font sizes for better readability */
          .feature-footer .follow-label { font-size: 16px; font-weight: 400; }
          .feature-footer .footer-link { font-size: 16px; }
        }
      `}</style>
      <main style={{ flex: 1, display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: 0 }}>
        <div style={{ width: '100%' }}>
          <section className="landing-section" style={{ background: colors.primary.main, color: '#fff', padding: '40px 70px' }}>
            <video ref={videoRef} className="landing-hero-video" autoPlay muted loop playsInline preload="auto" aria-hidden="true">
              <source src={heroVideo} type="video/mp4" />
            </video>
            <div className="landing-hero-overlay-base" aria-hidden="true"></div>
            <div className="landing-hero-overlay-tint" aria-hidden="true"></div>
            <header className="landing-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px 16px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img className="landing-logo" src={logo} alt="Blupons" style={{ height: 74 }} />
              </div>

              <div className="landing-language-selector">
                <LanguageSelector
                  language={language}
                  country={''}
                  countryOptions={countryOptions}
                  onLanguageChange={handleLanguageChange}
                  onCountryChange={handleCountryChange}
                  showCountry={false}
                />
              </div>
            </header>

            <div className="landing-content" style={{ maxWidth: 980, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <h2 className="landing-h2" style={{ marginTop: 30, marginBottom: 0, fontSize: 55, fontWeight: 700 }}>{t('landing.heroTitle')}</h2>
              <p className="landing-description" style={{ marginTop: 24, fontSize: 18, fontWeight: 500, maxWidth: 550 }}>
                {t('landing.heroDescription')}
              </p>
              <h3 className="landing-h3" style={{ marginTop: 16, fontSize: 24, fontWeight: 600, fontStyle: 'semibold' }}>{t('landing.downloadPrompt')}</h3>

              <div className="landing-store-buttons" style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Open Blupon on Google Play" style={{ display: 'inline-block' }}>
                    <img src={playImg} alt="Download on Google Play" style={{ height: 50 }} />
                  </a>
                  <img className="landing-qr-image landing-qr-container" src={playQr} alt="Play QR" style={{ height: 120, width: 120 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Open Blupon on App Store" style={{ display: 'inline-block' }}>
                    <img src={appstoreImg} alt="Download on App Store" style={{ height: 50 }} />
                  </a>
                  <img className="landing-qr-image landing-qr-container" src={appstoreQr} alt="App Store QR" style={{ height: 120, width: 120 }} />
                </div>
              </div>
            </div>
          </section>

          <section className="partners-section" style={{ background: '#fff', padding: '24px 0px' }} aria-label="partners">
            <div className="partners-row" style={{ width: '100%', maxWidth: '100%', margin: '0 auto', display: 'flex', gap: 0, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', padding: '0' }}>
              {partnerLogos.map((src, idx) => (
                  <div key={idx} className="partner-item" style={{ padding: '30px 24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img className="partner-img" src={src} alt={`partner-${idx}`} style={{ height: 48, objectFit: 'contain', display: 'block', maxWidth: '120px' }} />
                  </div>
              ))}
            </div>
          </section>

          <section className="savings-section" style={{ padding: '40px 24px 96px', background: '#f3f4f5' }} aria-label="savings">
            <div className="savings-inner" style={{ maxWidth: 1280, margin: '0 auto', textAlign: 'center' }}>
              <h2 className="savings-title" style={{ fontSize: 26, margin: '24px 0 40px', color: '#222', fontWeight: 600 }}>{t('savings.sectionTitle')}</h2>
              <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
                {savingsCards.map((c, idx) => (
                  <SavingsCard key={idx} icon={c.icon} title={c.title} description={c.description} dir={dir} />
                ))}
              </div>
            </div>
          </section>

          <section className="steps-section" style={{ background: '#f3f4f5' }} aria-label="steps">
            <h2 className="steps-title">{t('steps.sectionTitle')}</h2>
            <div className="steps-grid">
              {stepsCards.map((step, idx) => (
                <StepCard key={idx} number={idx + 1} title={step.title} description={step.description} />
              ))}
            </div>
            <div style={{ maxWidth: 980, margin: '48px auto', textAlign: 'center' }}>
              <a className="start-saving-button" href="#top" onClick={(event) => scrollToSection(event, topRef, 'start')} style={{
                display: 'inline-block',
                padding: '10px 24px',
                borderRadius: 25,
                background: colors.primary.main,
                color: '#fff',
                textDecoration: 'none',
                fontWeight: 500,
              }}>{dir === 'rtl' ? 'ابدأ التوفير الآن' : 'Start Saving Now'}</a>
            </div>
          </section>

          {isMobileGraphics ? (
            <GraphicsSectionMobile
              phoneBg={phoneBg}
              dir={dir}
              title={t('graphics.sectionTitle')}
              items={graphicsItems}
            />
          ) : (
            <GraphicsSectionDesktop
              phoneBg={phoneBg}
              dir={dir}
              title={t('graphics.sectionTitle')}
              items={graphicsItems}
            />
          )}

          <section className="built-section" aria-label="built" dir="ltr">
            <div className="built-content-wrapper">
              <div className="built-inner">
                <h3 className="built-title">{t('built.sectionTitle')}</h3>
                <div className="built-grid">
                  <div className="built-item">
                    <div className="built-number"><img src={riyalIcon} alt="" className="built-riyal" />{t('built.items.0.number')}</div>
                    <div className="built-desc">{t('built.items.0.title')}</div>
                  </div>
                  <div className="built-item">
                    <div className="built-number">{t('built.items.1.number')}</div>
                    <div className="built-desc">{t('built.items.1.title')}</div>
                  </div>
                  <div className="built-item">
                    <div className="built-number">{t('built.items.2.number')}</div>
                    <div className="built-desc">{t('built.items.2.title')}</div>
                  </div>
                </div>
                <div className="built-cta">
                  <a className="built-cta-button" href="#page-bottom" onClick={(event) => scrollToSection(event, bottomRef, 'end')}>{t('built.cta')}</a>
                </div>
              </div>
            </div>
          </section>

          <section className="feature-section" aria-label="feature">
            <div className="feature-container">
              <div className="feature-text">
                <h2 className="feature-title">{t('feature.title')}</h2>
                <p className="feature-description">{t('feature.description')}</p>
                <div className="feature-store-buttons">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Open Blupon on App Store" style={{ display: 'inline-block' }}>
                      <img className="feature-store-img" src={appstoreImg} alt="Download on App Store" />
                    </a>
                    <img className="feature-qr-container" src={appstoreQr} alt="App Store QR" style={{ height: 125, width: 125 }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Open Blupon on Google Play" style={{ display: 'inline-block' }}>
                      <img className="feature-store-img" src={playImg} alt="Download on Google Play" />
                    </a>
                    <img className="feature-qr-container" src={playQr} alt="Play QR" style={{ height: 125, width: 125 }} />
                  </div>
                </div>
              </div>
              <div className="feature-graphic">
                <img src={language === 'AR' ? featureImageAr : featureImageEn} alt="Blupon App" />
              </div>
                </div>
                <div className="feature-footer" aria-label="feature-footer">
                  <div className="feature-footer-left">
                    <div className="follow-label">{t('feature.followLabel')}</div>
                    <div className="social-icons">
                      <a href="https://x.com/blupons" target="_blank" rel="noopener noreferrer" aria-label="x"><img src={twitterIcon} alt="X" /></a>
                      <a href="https://www.instagram.com/bluponz" target="_blank" rel="noopener noreferrer" aria-label="instagram"><img src={instagramIcon} alt="Instagram" /></a>
                      <a href="https://www.tiktok.com/@blupons" target="_blank" rel="noopener noreferrer" aria-label="tiktok"><img src={tiktokIcon} alt="TikTok" /></a>
                    </div>
                  </div>

                  <div className="feature-footer-right">
                    <a className="footer-link" href="/terms-and-conditions">{t('feature.terms')}</a>
                    <a className="footer-link" href="/privacy-policy">{t('feature.privacy')}</a>
                  </div>
                </div>
          </section>

          
        </div>
      </main>
      <div ref={bottomRef} />
    </div>
  );
};

export default Landing;
